// Billing Cycle Types
export interface BillingCycle {
  id: string;
  name: string;
  months: number;
  displayName: string;
  type?: 'standard' | 'custom';
  isDefault?: boolean;
  isShortest?: boolean;
}

export interface ProductBillingCycles {
  productId: string;
  availableCycles: BillingCycle[];
  shortestCycleId: string;
}

// Credit Management Types
export type CreditLifecycleMode = 'reset' | 'accumulate';

export interface CreditManagementSettings {
  productId: string;
  lifecycleMode: CreditLifecycleMode;
  expiryBillingCycles?: number; // Only for accumulate mode
}

// Plan Pricing Types
export interface MultiCurrencyPrice {
  [currency: string]: number; // e.g. { USD: 99, EUR: 89, GBP: 79 }
}

export interface PlanPricing {
  billingCycleId: string;
  prices: MultiCurrencyPrice; // Prices for all active currencies
  defaultCurrency: string;
  stripeProductId?: string;
  stripePriceIds?: { [currency: string]: string }; // Stripe price IDs per currency
  discountPercentage?: number; // Calculated from base price
  introPrices?: MultiCurrencyPrice;
  recurringPrices?: MultiCurrencyPrice;
  introCycles?: number;
}

export interface PlanCreditSettings {
  includeCredits: boolean;
  creditsPerCycle?: number; // Per shortest billing cycle
  allowTopUp: boolean;
  topUpPricesPerCredit?: MultiCurrencyPrice; // Price per credit for all currencies
}

// Translation Types
export interface MultiLanguageString {
  [locale: string]: string; // e.g. { en: 'Basic Plan', de: 'Basispaket', es: 'Plan Básico' }
}

export interface MultiLanguageStringArray {
  [locale: string]: string[]; // e.g. { en: ['Feature 1', 'Feature 2'], de: ['Funktion 1', 'Funktion 2'] }
}

// Extended Plan Types
export interface SubscriptionPlan {
  id: string;
  productId: string;
  parentPlanId?: string | null;
  isDefault?: boolean;
  isVariant?: boolean;
  // Controls whether the plan appears in customer-facing catalogs/checkout.
  isAvailableInCheckout?: boolean;
  name: string;
  nameTranslations?: MultiLanguageString;
  description?: string;
  descriptionTranslations?: MultiLanguageString;
  translations?: {
    name?: MultiLanguageString;
    display_name?: MultiLanguageString;
    description?: MultiLanguageString;
    features?: Array<{ [locale: string]: string }> | MultiLanguageStringArray;
  };
  type: 'individual' | 'teams';
  pricing: PlanPricing[]; // Multiple prices for different billing cycles
  prices?: MultiCurrencyPrice; // UI compatibility - prices for current context
  creditSettings: PlanCreditSettings;
  features: string[];
  featuresTranslations?: MultiLanguageStringArray;
  featureFlags?: { [key: string]: boolean };
  isRecommended?: boolean;
  // Teams specific
  includedSeats?: number;
  minSeats?: number;
  maxSeats?: number;
  pricesPerAdditionalSeat?: MultiCurrencyPrice; // Prices per seat for all currencies
  // Settings
  freeTrialDays?: number;
  trialRequiresCard?: boolean;
  prorateChanges?: boolean;
  allowCancellation?: boolean;
  status: 'draft' | 'active' | 'archived';
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
  
  // Backend snake_case fields (for compatibility)
  pricesArray?: Array<{
    id?: string;
    billing_cycle_id?: string;
    billingCycleId?: string;
    currency?: string;
    base_price?: number;
    basePrice?: number;
    intro_price?: number;
    introPrice?: number;
    recurring_price?: number;
    recurringPrice?: number;
    intro_cycles?: number;
    introCycles?: number;
    setup_fee?: number;
    per_seat_price?: number | null;
    stripe_price_id?: string | null;
    is_active?: boolean;
  }>;
  credit_settings?: {
    id?: string;
    includes_credits?: boolean;
    credits_per_cycle?: number;
    credits_rollover?: boolean;
    max_rollover_months?: number | null;
    allow_purchase?: boolean;
    discount_percentage?: string;
    top_up_prices_per_credit?: unknown | null;
  };
  // Backend compatibility fields
  plan_type?: 'individual' | 'teams';
  planType?: 'individual' | 'teams';
  trial_days?: number;
  trialDays?: number;
  trial_requires_card?: boolean;
  is_recommended?: boolean;
  prorate_changes?: boolean;
  allow_cancellation?: boolean;
  included_seats?: number;
  min_seats?: number;
  max_seats?: number;
  parent_plan_id?: string | null;
  is_default?: boolean;
  is_variant?: boolean;
  is_available_in_checkout?: boolean;
}

export interface CreditPackage {
  id: string;
  productId: string;
  name: string;
  nameTranslations?: MultiLanguageString;
  description?: string;
  descriptionTranslations?: MultiLanguageString;
  credits: number;
  prices: MultiCurrencyPrice; // Prices for all active currencies
  defaultCurrency: string;
  pricePerCredit?: { [currency: string]: number }; // Calculated per currency
  features?: string[];
  featuresTranslations?: MultiLanguageStringArray;
  stripeProductId?: string;
  stripePriceIds?: { [currency: string]: string };
  featureFlags?: { [key: string]: boolean };
  isRecommended?: boolean;
  offerFreeTrial?: boolean;
  freeTrialDays?: number;
  // Backend compatibility fields
  trial_days?: number;
  trialDays?: number;
  trial_requires_card?: boolean;
  trialRequiresCard?: boolean;
  is_recommended?: boolean;
  status: 'draft' | 'active' | 'archived';
  plan_type?: 'individual' | 'teams';
  createdAt: string;
  updatedAt: string;
  translations?: {
    name?: MultiLanguageString;
    display_name?: MultiLanguageString;
    description?: MultiLanguageString;
    features?: Array<{ [locale: string]: string }> | MultiLanguageStringArray;
  };
}

export interface OneTimePaymentPlan {
  id: string;
  productId: string;
  name: string;
  nameTranslations?: MultiLanguageString;
  description?: string;
  descriptionTranslations?: MultiLanguageString;
  type: 'individual' | 'teams';
  prices: MultiCurrencyPrice; // Prices for all active currencies
  pricesArray?: Array<Record<string, unknown>>; // Raw prices data from API
  defaultCurrency: string;
  accessDurationMonths: number | 'unlimited';
  isLifetime?: boolean; // Backend field for lifetime access
  features: string[];
  featuresArray?: Array<Record<string, unknown>>; // Raw features data from API
  featuresTranslations?: MultiLanguageStringArray;
  featureFlags?: { [key: string]: boolean };
  stripeProductId?: string;
  stripePriceIds?: { [currency: string]: string };
  // Teams specific
  includedSeats?: number;
  pricesPerAdditionalSeat?: MultiCurrencyPrice; // Prices per seat for all currencies
  // Trial settings
  isRecommended?: boolean;
  offerFreeTrial?: boolean;
  freeTrialDays?: number;
  // Backend compatibility fields
  trial_days?: number;
  trialDays?: number;
  is_popular?: boolean;
  trial_requires_card?: boolean;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown; // Allow additional fields for debugging
}

// Predefined Billing Cycles
export const DEFAULT_BILLING_CYCLES: BillingCycle[] = [
  { id: 'monthly', name: 'Monthly', months: 1, displayName: 'Monthly' },
  { id: 'quarterly', name: 'Quarterly', months: 3, displayName: 'Every 3 months' },
  { id: 'semi-annual', name: 'Semi-Annual', months: 6, displayName: 'Every 6 months' },
  { id: 'annual', name: 'Annual', months: 12, displayName: 'Yearly' },
  { id: 'biennial', name: 'Biennial', months: 24, displayName: 'Every 2 years' }
];

// Helper functions
export function calculateMonthlyPrice(price: number, billingCycleMonths: number): number {
  return price / billingCycleMonths;
}

export function calculateDiscountPercentage(originalPrice: number, discountedPrice: number): number {
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
}

export function formatBillingCycle(cycle: BillingCycle): string {
  if (cycle.months === 1) return 'per month';
  if (cycle.months === 12) return 'per year';
  return `per ${cycle.months} months`;
}

export function formatPriceWithCycle(price: number, cycle: BillingCycle, currency = 'USD'): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  
  return `${formatter.format(price)} ${formatBillingCycle(cycle)}`;
}

// Translation Support Types
export interface TranslationMetadata {
  is_machine_translated?: boolean;
  provider?: string;
  is_verified?: boolean;
  verified_by?: string;
  verified_at?: string;
}

export interface TranslatedSubscriptionPlan extends SubscriptionPlan {
  display_name?: string;
  translation_metadata?: Record<string, TranslationMetadata>;
}

export interface TranslatedCreditPackage extends CreditPackage {
  translations?: {
    name?: MultiLanguageString;
    description?: MultiLanguageString;
  };
  translation_metadata?: Record<string, TranslationMetadata>;
}

export interface TranslatedOneTimePlan extends OneTimePaymentPlan {
  translations?: {
    name?: MultiLanguageString;
    description?: MultiLanguageString;
  };
  translation_metadata?: Record<string, TranslationMetadata>;
}

export interface TranslationParams {
  lang?: string;
  includeTranslations?: boolean | string | string[];
}
